import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'

import {
  formatCurrency,
  getFloatNumberFromInputEvent,
} from '../../../utils/currencyFormatting'
import { useTranslation } from '../../../context/translationProvider'
import { StyledFormWrapper, StyledInputWrapper } from './styles'
import { Field } from '../field'
import Tooltip from '../../atoms/tooltip'
import { InfoIcon } from '../../atoms/InfoIcon'
import { Button } from '../../atoms/button'
import { WithdrawBankAccountList } from '../../organisms/WithdrawBankAccountList'
import { PaymentTypes } from '../../../utils/constants'
import {
  getLivenessStatus,
  requestBankAccountWithdraw,
} from '../../../adapters/payments'
import { toast } from '../../atoms'
import { usePreDepositModals } from '../../../hooks/usePreDepositModals'

export const BankAccountWithdrawForm = ({
  method,
  minimumAmount,
  maximumAmount,
  bankAccounts = [],
}) => {
  const { translate } = useTranslation()
  const [withdrawAmount, setWithdrawAmount] = useState(0)
  const [selectedBank, setSelectedBank] = useState()
  const { openLivenessModal } = usePreDepositModals()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
  })

  useEffect(() => {
    if (bankAccounts.length === 1) {
      setSelectedBank(bankAccounts[0])
    }
  }, [bankAccounts])

  const onChange = (e) => {
    const value = getFloatNumberFromInputEvent(e.target?.value)
    setWithdrawAmount(value)
  }

  const onWithdraw = async () => {
    const response = await requestBankAccountWithdraw({
      bankAccountId: selectedBank.id,
      amount: Number(withdrawAmount),
      paymentMethodDetailId: method.paymentMethodDetailId,
    })

    if (!response.ok) {
      toast.error(response.error.message)
      return
    }

    const {
      data: {
        liveness: { url, onboardingId },
      },
    } = response

    openLivenessModal(
      selectedBank,
      () => {},
      () => pollLivenessStatus(onboardingId),
      PaymentTypes.WITHDRAW,
      url
    )
  }

  const pollLivenessStatus = async (onboardingId) => {
    const response = await getLivenessStatus(onboardingId)

    if (!response.ok) {
      toast.error(response.error.message)
      return
    }

    const {
      data: { status },
    } = response

    if (status === 'APPROVED') {
      toast.success(translate('payments.withdraw.success'))
      return
    }

    if (status === 'REPROVED') {
      toast.error(translate('payments.receipt.error'))
      return
    }

    setTimeout(() => pollLivenessStatus(onboardingId), 500)
  }

  const isValidAmount =
    withdrawAmount >= minimumAmount && withdrawAmount <= maximumAmount

  return (
    <StyledFormWrapper>
      <form onSubmit={handleSubmit(onWithdraw)}>
        <StyledInputWrapper>
          <Field
            label={translate('payments.amount')}
            forceValue={formatCurrency(withdrawAmount, {
              useGrouping: false,
            })}
            fieldValue={formatCurrency(withdrawAmount, {
              useGrouping: false,
            })}
            inputmode="numeric"
            errors={errors}
            onChange={onChange}
            formProperties={register('withdrawAmount', {
              required: true,
              min: minimumAmount,
              max: maximumAmount,
            })}
          />

          <Tooltip
            text={translate('withdrawals.minimumWagerWithdrawal')}
            textPosition="bottom"
            style={{ position: 'absolute', right: 0, top: 0 }}
          >
            <InfoIcon />
          </Tooltip>
        </StyledInputWrapper>

        <WithdrawBankAccountList
          selectedBank={selectedBank}
          setSelectedBank={setSelectedBank}
          bankAccounts={bankAccounts}
        />

        <Button expand disabled={!isValidAmount} id="btnWithdrawalModal">
          {translate('realityCheck.continue')} &nbsp;
        </Button>
      </form>
    </StyledFormWrapper>
  )
}

BankAccountWithdrawForm.propTypes = {
  method: PropTypes.object,
  minimumAmount: PropTypes.number,
  maximumAmount: PropTypes.number,
  bankAccounts: PropTypes.array,
}
